// Pi Video Modal JavaScript
// Vanilla JavaScript implementation for video popup functionality

class PiVideoModal {
  constructor() {
    this.modal = null;
    this.videoIframe = null;
    this.closeButton = null;
    this.overlay = null;
    this.thumbnails = [];
    this.isOpen = false;
    this.currentVideoUrl = null;
    
    this.init();
  }
  
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupModal());
    } else {
      this.setupModal();
    }
  }
  
  setupModal() {
    this.modal = document.getElementById('pi-video-modal');
    if (!this.modal) {
      console.warn('Pi Video Modal: Modal element not found');
      return;
    }
    
    this.videoIframe = this.modal.querySelector('.pi-video-modal__video');
    this.closeButton = this.modal.querySelector('.pi-video-modal__close');
    this.overlay = this.modal.querySelector('.pi-video-modal__overlay');
    
    // Get all video thumbnails
    this.thumbnails = document.querySelectorAll('.pi-video-thumbnail');
    
    if (this.thumbnails.length === 0) {
      console.warn('Pi Video Modal: No video thumbnails found');
      return;
    }
    
    this.bindEvents();
    this.addAccessibilityFeatures();
  }
  
  bindEvents() {
    // Bind click events to thumbnails
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', (e) => this.openModal(e));
      
      // Add keyboard support for thumbnails
      thumbnail.setAttribute('tabindex', '0');
      thumbnail.setAttribute('role', 'button');
      thumbnail.setAttribute('aria-label', `Play video ${index + 1}`);
      
      thumbnail.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.openModal(e);
        }
      });
    });
    
    // Bind close events
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.closeModal());
    }
    
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closeModal());
    }
    
    // Bind escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeModal();
      }
    });
    
    // Prevent modal from closing when clicking inside video container
    const videoContainer = this.modal.querySelector('.pi-video-modal__video-container');
    if (videoContainer) {
      videoContainer.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }
  }
  
  addAccessibilityFeatures() {
    // Add ARIA attributes
    this.modal.setAttribute('role', 'dialog');
    this.modal.setAttribute('aria-modal', 'true');
    this.modal.setAttribute('aria-hidden', 'true');
    
    if (this.closeButton) {
      this.closeButton.setAttribute('aria-label', 'Close video modal');
    }
  }
  
  openModal(event) {
    const thumbnail = event.currentTarget;
    const videoUrl = thumbnail.getAttribute('data-video-url');
    
    if (!videoUrl) {
      console.warn('Pi Video Modal: No video URL found on thumbnail');
      return;
    }
    
    if (!this.videoIframe) {
      console.error('Pi Video Modal: Video iframe not found');
      return;
    }
    
    // Convert video URL to embed URL
    const embedUrl = this.convertToEmbedUrl(videoUrl);
    
    if (!embedUrl) {
      console.warn('Pi Video Modal: Could not convert video URL to embed URL:', videoUrl);
      return;
    }
    
    this.currentVideoUrl = embedUrl;
    this.videoIframe.src = embedUrl;
    this.modal.classList.add('show');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.isOpen = true;
    
    // Focus management
    this.closeButton.focus();
    
    // Add loading state
    this.videoIframe.addEventListener('load', () => {
      this.videoIframe.classList.add('loaded');
    }, { once: true });
  }
  
  closeModal() {
    if (!this.modal || !this.isOpen) return;
    
    this.modal.classList.remove('show');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.isOpen = false;
    
    // Stop video by clearing src
    if (this.videoIframe) {
      this.videoIframe.src = '';
      this.videoIframe.classList.remove('loaded');
    }
    
    this.currentVideoUrl = null;
    
    // Return focus to the thumbnail that opened the modal
    const activeThumbnail = document.activeElement;
    if (activeThumbnail && activeThumbnail.classList.contains('pi-video-thumbnail')) {
      activeThumbnail.focus();
    }
  }
  
  convertToEmbedUrl(url) {
    if (!url) return null;
    
    try {
      // Clean URL
      url = url.trim();
      
      // YouTube URL conversion
      if (url.includes('youtube.com/watch') || url.includes('youtu.be/')) {
        let videoId = '';
        
        if (url.includes('youtube.com/watch')) {
          const urlObj = new URL(url);
          videoId = urlObj.searchParams.get('v');
        } else if (url.includes('youtu.be/')) {
          const urlObj = new URL(url);
          videoId = urlObj.pathname.split('/')[1];
        }
        
        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1`;
        }
      }
      
      // Vimeo URL conversion
      if (url.includes('vimeo.com/')) {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/');
        const videoId = pathParts[pathParts.length - 1];
        
        if (videoId && !isNaN(videoId)) {
          return `https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0`;
        }
      }
      
      // Instagram URL conversion (basic support)
      if (url.includes('instagram.com/p/') || url.includes('instagram.com/reel/')) {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/');
        const postId = pathParts[2]; // p or reel
        
        if (postId) {
          return `https://www.instagram.com/p/${postId}/embed/`;
        }
      }
      
      // TikTok URL conversion (basic support)
      if (url.includes('tiktok.com/')) {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/');
        const videoId = pathParts[pathParts.length - 1];
        
        if (videoId) {
          return `https://www.tiktok.com/embed/${videoId}`;
        }
      }
      
      console.warn('Pi Video Modal: Unsupported video URL format:', url);
      return null;
      
    } catch (error) {
      console.error('Pi Video Modal: Error parsing video URL:', error);
      return null;
    }
  }
  
  // Public method to refresh thumbnails (useful for dynamic content)
  refreshThumbnails() {
    this.thumbnails = document.querySelectorAll('.pi-video-thumbnail');
    this.bindEvents();
  }
}

// Initialize the video modal when the script loads
document.addEventListener('DOMContentLoaded', () => {
  window.piVideoModal = new PiVideoModal();
});

// Also initialize immediately if DOM is already loaded
if (document.readyState !== 'loading') {
  window.piVideoModal = new PiVideoModal();
}
